import React, { useMemo, useState, useEffect } from 'react';
import { Button, Col, Container, Form, FormGroup, Input, Label, Row, Modal, ModalBody, ModalHeader, Card, CardBody, CardHeader, Spinner, ModalFooter } from 'reactstrap';
import ContractorService from '../../../../../api/contractorService';
import { errorToast, successToast } from '../../../../../_helper/helper';
import DataTable from 'react-data-table-component';
import { FiUsers, FiAlertTriangle } from 'react-icons/fi'; // Alert icon added
import { StyledCard, EmptyStateContainer, EmptyStateIcon } from './styledCards';
import AreaService from '../../../../../api/areaService';
import { CiEdit } from "react-icons/ci";
import { MdDelete } from "react-icons/md";

const initialForm = { name: '', email: '', mobile_no: '', status: 'yes', factory_id: '', area_id: '' };

const ContractorSetup = () => {
  const [form, setForm] = useState(initialForm);
  const [contractors, setContractors] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const isEditing = editingIndex >= 0;
  const [showForm, setShowForm] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [mobileError, setMobileError] = useState('');
  
  const [Allfactories, setAllfactories] = useState([]);
  const [availableAreas, setAvailableAreas] = useState([]);

  // --- NEW STATE FOR DELETE ---
  const [deleteModal, setDeleteModal] = useState(false);
  const [contractorToDelete, setContractorToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f8f7fa',
        borderBottom: '1px solid #e3e2e7',
        fontSize: '0.875rem',
        fontWeight: '600',
        color: '#5e5873',
        padding: '12px 16px',
      },
    },
    headCells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '16px',
        paddingBottom: '16px',
      },
    },
    rows: {
      style: {
        fontSize: '0.9rem',
        minHeight: '60px',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid #f3f2f7',
        },
        '&:hover': {
          backgroundColor: '#f8f7fa !important',
        },
      },
    },
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      cell: row => <div className="font-weight-bold">{row.name}</div>,
      style: { paddingLeft: '3px' },
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: false,
    },
    {
      name: 'Mobile No',
      selector: row => row.mobile_no,
      sortable: false,
    },
    {
      name: 'Factory Name',
      selector: row => row.factory_name,
      sortable: false,
    },
    {
      name: 'Area Name',
      selector: row => row.area_name,
      sortable: false,
    },
    {
      name: 'Status',
      cell: row => (
        <div className="d-flex align-items-center justify-content-center">
          <Label className="switch">
            <Input
              type="checkbox"
              checked={row.active}
              onChange={() => toggleContractorStatus(row.id)}
            />
            <span className={`switch-state`} style={{ height: '25px', width: '45px' }}></span>
          </Label>
        </div>
      ),
    },
    {
      name: 'Update',
      cell: row => (
        <>
        <div className='d-flex gap-2'>
          <CiEdit 
            size={20} 
            className='f-light cursor-pointer' 
            style={{cursor: 'pointer'}} 
            onClick={() => startEdit(row)} 
          />
          {/* Updated onClick to call confirmDelete */}
          <MdDelete 
            size={20} 
            className='text-danger cursor-pointer' 
            style={{cursor: 'pointer'}} 
            onClick={() => confirmDelete(row)} 
          />
        </div>
        </>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const filtered = useMemo(() => {
    let filteredContractors = contractors;
    if (query) {
      const q = query.toLowerCase();
      filteredContractors = contractors.filter((c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.mobile_no && c.mobile_no.toLowerCase().includes(q))
      );
    }
    return filteredContractors.reverse();
  }, [contractors, query]);

  const canSubmit = useMemo(() => {
    const emailValid = /[^\s@]+@[^\s@]+\.com$/.test(form.email.toLowerCase());
    const nameValid = form.name.trim().length > 1;
    const mobileValid = /^\d{4}-\d{7}$/.test(form.mobile_no);
    
    const factoryValid = form.factory_id !== '' && form.factory_id !== null;
    const areaValid = form.area_id !== '' && form.area_id !== null;
    if (isEditing) {
      return emailValid && nameValid && mobileValid && factoryValid && areaValid && (form.status === 'yes' || form.status === 'no');
    } else {
      return emailValid && nameValid && mobileValid && factoryValid && areaValid;
    }
  }, [form, isEditing]);

  useEffect(() => {
    fetchContractors();
    fetchAllfactories();
  }, []);

  const fetchAllfactories = async () => {
    try {
      const response = await AreaService.getAllFactoriesDataForIT();
      if (response?.status === 200) {
        setAllfactories(response.data.data);
      }
    } catch (error) {
      console.log("ERROR:", error);
    }
  }

  const fetchContractors = async () => {
    setLoading(true);
    try {
      const response = await ContractorService.getAllContractors();
      if (response?.status === 200) {
        const contractorsData = response.data?.contractors || response.data?.data || response.data || [];
        setContractors(contractorsData);
      } else {
        errorToast('Failed to fetch contractors');
      }
    } catch (error) {
      console.error('Error fetching contractors:', error);
      errorToast('Error loading contractors');
    } finally {
      setLoading(false);
    }
  };

  const addContractor = async (contractorData) => {
    try {
      const payload = {
        name: contractorData.name,
        email: contractorData.email,
        mobile_no: contractorData.mobile_no,
        factory_id: contractorData.factory_id,
        area_id: contractorData.area_id
      };
      const response = await ContractorService.addContractor(payload);
      if (response?.status == 200) {
        successToast('Contractor added successfully');
        fetchContractors();
        setShowForm(false)
        return true;
      } else {
        errorToast(response?.data?.message || 'Failed to add contractor');
        return false;
      }
    } catch (error) {
      console.error('Error adding contractor:', error);
      errorToast('Error adding contractor');
      return false;
    }
  };

  const updateContractor = async (contractorData) => {
    try {
      const payload = {
        id: editingContractor.id,
        name: contractorData.name,
        email: contractorData.email,
        mobile_no: contractorData.mobile_no,
        factory_id: contractorData.factory_id,
        area_id: contractorData.area_id
      };

      const response = await ContractorService.updateContractor(payload);
      if (response?.status === 200) {
        successToast('Contractor updated successfully');
        fetchContractors();
        return true;
      } else {
        errorToast('Failed to update contractor');
        return false;
      }
    } catch (error) {
      console.error('Error updating contractor:', error);
      errorToast('Error updating contractor');
      return false;
    }
  };

  const toggleContractorStatus = async (contractorId) => {
    try {
      const response = await ContractorService.toggleContractorStatus(contractorId);
      if (response?.status === 200) {
        successToast('Contractor status updated successfully');
        fetchContractors();
      } else {
        errorToast('Failed to update contractor status');
      }
    } catch (error) {
      console.error('Error toggling contractor status:', error);
      errorToast('Error updating contractor status');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name == 'factory_id') {
        const selectedId = parseInt(value);
        const selectedFactory = Allfactories.find(f => f.factory_id == selectedId);
        setAvailableAreas(selectedFactory?.areas || []); 
        setForm((prev) => ({ ...prev, factory_id: value, area_id: '' }));
        return;
    }

    if (name === 'mobile_no') {
      const mobilePattern = /^\d{4}-\d{7}$/;
      if (value && !mobilePattern.test(value)) {
        setMobileError('Mobile number must be in format: 1234-567890');
      } else {
        setMobileError('');
      }
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingIndex(-1);
    setEditingContractor(null);
    setShowForm(false);
    setMobileError('');
    setAvailableAreas([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      if (!isEditing) {
        const emailExists = contractors.some((c) => c.email.toLowerCase() === form.email.toLowerCase());
        if (emailExists) {
          errorToast('A contractor with this email already exists.');
          setSubmitting(false);
          return;
        }
      }
      let success = false;
      if (isEditing) {
        success = await updateContractor(form);
      } else {
        success = await addContractor(form);
      }
      if (success) {
        if (isEditing) {
          resetForm();
        } else {
          setForm(initialForm);
          setAvailableAreas([]);
        }
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      errorToast('An error occurred while processing your request');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (contractor) => {
    setEditingContractor(contractor);
    setEditingIndex(0);
    const factoryId = contractor.factory_id || ''; 
    const selectedFactory = Allfactories.find(f => f.factory_id == factoryId);
    setAvailableAreas(selectedFactory?.areas || []);
    
    setForm({
      name: contractor.name,
      email: contractor.email,
      mobile_no: contractor.mobile_no || '',
      status: contractor.active ? 'yes' : 'no',
      factory_id: factoryId,
      area_id: contractor.area_id || '' 
    });
    setShowForm(true);
  };

  const confirmDelete = (contractor) => {
    setContractorToDelete(contractor);
    setDeleteModal(true);
  };

  const handleDeleteContractor = async () => {
    if (!contractorToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await ContractorService.deleteContractor(contractorToDelete.id);
      
      if (response?.status === 200) {
        successToast("Contractor deleted successfully");
        fetchContractors();
        setDeleteModal(false);
        setContractorToDelete(null);
      } else {
        errorToast(response?.data?.message || "Failed to delete contractor");
        setDeleteModal(false); 
      }
    } catch (error) {
      console.error("Delete Error:", error);
      errorToast("Something went wrong while deleting");
    } finally {
      setIsDeleting(false);
    }
  };


  return (
    <Container fluid className='py-3 px-3'>
      <h4 className='mb-3'>Contractor Management</h4>

      <StyledCard>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h5>Contractor List</h5>
            <p className="badge badge-light-primary mx-0 mb-0 mt-2" style={{ fontSize: '12px' }}>
              Showing {contractors.length} contractors
            </p>
          </div>
          <div className='d-flex gap-2'>
            <Input
              placeholder='Filter contractors...'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ maxWidth: 260 }}
            />
            <Button
              color="primary"
              onClick={() => { setShowForm(true); setEditingIndex(-1); setForm(initialForm); setAvailableAreas([]); }}
              style={{ minWidth: '180px' }}
            >
              Add Contractor
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner color="primary" />
            </div>
          ) : filtered.length <= 0 ? (
            <EmptyStateContainer>
              <EmptyStateIcon>
                <FiUsers size={48} />
              </EmptyStateIcon>
              <h4 className="mb-1">
                {query ? 'No Contractors Found' : 'No Contractors'}
              </h4>
              <p className="text-muted mb-0">
                {query
                  ? 'No contractors match your search criteria.'
                  : 'There are currently no contractors in the system.'}
              </p>
            </EmptyStateContainer>
          ) : (
            <DataTable
              columns={columns}
              data={filtered}
              pagination
              highlightOnHover
              striped
              noHeader
              responsive
              customStyles={customStyles}
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </CardBody>
      </StyledCard>

      {/* --- ADD/EDIT MODAL --- */}
      <Modal isOpen={showForm} toggle={() => setShowForm((s) => !s)}>
        <ModalHeader toggle={() => setShowForm((s) => !s)}>
          {isEditing ? 'Edit Contractor' : 'Add Contractor'}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <Row className='g-3'>
              <Col md='12'>
                <FormGroup>
                  <Label for='factory_id'>Select Factory</Label>
                  <Input 
                    type='select' 
                    id='factory_id' 
                    name='factory_id' 
                    value={form.factory_id} 
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a Factory</option>
                    {Allfactories.map((factory) => (
                      <option key={factory.factory_id} value={factory.factory_id}>
                        {factory.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md='12'>
                <FormGroup>
                  <Label for='area_id'>Select Area</Label>
                  <Input 
                    type='select' 
                    id='area_id' 
                    name='area_id' 
                    value={form.area_id} 
                    onChange={handleChange}
                    required
                    disabled={!form.factory_id} 
                  >
                    <option value="">Select an Area</option>
                    {availableAreas.length > 0 ? (
                        availableAreas.map((area) => (
                        <option key={area.id} value={area.id}>
                            {area.area}
                        </option>
                        ))
                    ) : (
                        <option disabled>No areas available</option>
                    )}
                  </Input>
                </FormGroup>
              </Col>

              <Col md='12'>
                <FormGroup>
                  <Label for='name'>Contractor Name</Label>
                  <Input id='name' name='name' placeholder='Enter name' value={form.name} onChange={handleChange} required />
                </FormGroup>
              </Col>
              <Col md='12'>
                <FormGroup>
                  <Label for='email'>Email</Label>
                  <Input id='email' name='email' type='email' placeholder='name@example.com' value={form.email} onChange={handleChange} required />
                </FormGroup>
              </Col>
              <Col md='12'>
                <FormGroup>
                  <Label for='mobile_no'>Mobile Number</Label>
                  <Input
                    id='mobile_no'
                    name='mobile_no'
                    type='tel'
                    placeholder='1234-567890'
                    value={form.mobile_no}
                    onChange={handleChange}
                    required
                    invalid={!!mobileError}
                  />
                  {mobileError && (
                    <div className="invalid-feedback d-block">
                      {mobileError}
                    </div>
                  )}
                </FormGroup>
              </Col>
              {isEditing && (
                <Col md='12'>
                  <FormGroup>
                    <Label for='status'>Status</Label>
                    <Input id='status' name='status' type='select' value={form.status} onChange={handleChange}>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </Input>
                  </FormGroup>
                </Col>
              )}
              <Col md='12' className='d-flex justify-content-end gap-2'>
                <Button type='button' color='secondary' outline onClick={resetForm} disabled={submitting}>Cancel</Button>
                <Button color='primary' type='submit' disabled={!canSubmit || submitting}>
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {isEditing ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    isEditing ? 'Update' : 'Add'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>

      {/* --- DELETE CONFIRMATION MODAL --- */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)} className="bg-transparent">
             Delete Confirmation
        </ModalHeader>
        <ModalBody className="text-center p-4">
            <div className="text-warning mb-3">
                <FiAlertTriangle size={50} />
            </div>
            <h4>Are you sure?</h4>
            <p className="text-muted">
                Do you really want to delete contractor <strong>{contractorToDelete?.name}</strong>? 
                <br/> This process cannot be undone.
            </p>
        </ModalBody>
        <ModalFooter className="justify-content-center border-0 pt-0">
            <Button color="dark" outline onClick={() => setDeleteModal(false)} disabled={isDeleting}>
                Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteContractor} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
        </ModalFooter>
      </Modal>

    </Container>
  );
};

export default ContractorSetup;